import React, { useState } from "react";
import { useSelector } from "react-redux";
import PersonalInfoTab from "./ConfigTab/PersonalInfoTab";
import BankInfoTab from "./ConfigTab/BankInfoTab";
import PackInfoTab from "./ConfigTab/PackInfoTab";
import EditBankModal from "./ConfigModal/EditBankModal";
import CreateBankModal from "./ConfigModal/CreateBankModal";
import DeleteBankModal from "./ConfigModal/DeleteBankModal";
import SelectBankModal from "./ConfigModal/SelectBankModal";

import { useGetUserByIdQuery } from "store/user/userApi";

import {
  useGetBanksByCompanyQuery,
  useUpdateBankMutation,
  useDeleteBankMutation,
  useCreateBankMutation,
} from "store/bank/bank.api";
import { useTranslation } from "react-i18next";
import { toast } from "react-toastify";

const CompanyConfig = () => {
  const { t } = useTranslation();
  const [bankToSelect, setBankToSelect] = useState(null);
  const [showSelectModal, setShowSelectModal] = useState(false);
  const [selectedBankId, setSelectedBankId] = useState(null);
  const [editingBank, setEditingBank] = useState(null);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [bankToDelete, setBankToDelete] = useState(null);
  const [showDeleteWarningModal, setShowDeleteWarningModal] = useState(false);
  const [activeTab, setActiveTab] = useState("info");

  const authUser = useSelector((state) => state.auth.user);

  const [updateBank] = useUpdateBankMutation();
  const [deleteBank] = useDeleteBankMutation();
  const [createBank, error] = useCreateBankMutation();

  const userId = authUser?._id;

  const {
    data: currentUser,
    isLoading: isUserLoading,
    isError: isUserError,
  } = useGetUserByIdQuery(userId, { skip: !userId });

  const companyId =
    currentUser?.data?.companyId?._id || currentUser?.data?.companyId;

  const {
    data: banksData,
    isLoading: isBanksLoading,
    isError: isBanksError,
    refetch,
  } = useGetBanksByCompanyQuery(companyId, { skip: !companyId });

  const handleSelectBank = async (clickedBank) => {
    const allBanks = banksData?.data || [];

    for (const bank of allBanks) {
      const newStatus = bank._id === clickedBank._id;
      if (bank.status !== newStatus) {
        await updateBank({ id: bank._id, data: { status: newStatus } });
      }
    }

    setSelectedBankId(clickedBank._id);
    refetch();
  };

  const handleDeleteBank = async (bank) => {
    if (window.confirm(t("profile.deleteConfirmation"))) {
      await deleteBank(bank._id);
      refetch();
    }
  };

  const handleSaveEdit = async (id, updatedData) => {
    await updateBank({ id, data: updatedData });
    setEditingBank(null);
    refetch();
  };

  const handleCreateBank = async (newBankData) => {
    const dataWithCompany = { ...newBankData, companyId, status: false };

    await createBank(dataWithCompany).unwrap();

    refetch();
  };

  const handleRequestDeleteBank = (bank) => {
    if (bank.status === true) {
      setShowDeleteWarningModal(true);
    } else {
      setBankToDelete(bank);
    }
  };

  const handleConfirmDeleteBank = async (bank) => {
    try {
      await deleteBank(bank._id);
      setBankToDelete(null);
      refetch();
    } catch (error) {
      console.error("Erreur lors de la suppression :", error);
    }
  };

  const handleConfirmSelectBank = async () => {
    if (!bankToSelect) return;
    const allBanks = banksData?.data || [];

    for (const bank of allBanks) {
      const newStatus = bank._id === bankToSelect._id;
      if (bank.status !== newStatus) {
        await updateBank({ id: bank._id, data: { status: newStatus } });
      }
    }

    setSelectedBankId(bankToSelect._id);
    setBankToSelect(null);
    setShowSelectModal(false);
    refetch();
  };

  if (!userId || isUserLoading) return <p>{t("profile.loadingUser")}</p>;
  if (isUserError) return <p>{t("profile.errorUser")}</p>;

  return (
    <div className="text-white min-vh-100 mt-5">
      <div className="container mt-n5 position-relative z-index-1">
        <div className="row rounded p-4 shadow">
          {/* Left Panel */}
          <div className="col-md-4 text-center mb-4 mb-md-0">
            <img
              src={currentUser.data.image}
              alt={currentUser.data.name}
              className="rounded-circle border border-4 border-primary mb-3"
              style={{ width: "120px", height: "120px", objectFit: "cover" }}
            />
            <h4 className="text-dark">{currentUser.data.name}</h4>
            <p className="text-muted">{currentUser.data.email}</p>
            <span
              className={`badge ${
                currentUser.data.status === "enabled"
                  ? "bg-success"
                  : "bg-danger"
              }`}>
              {currentUser.data.status === "enabled"
                ? t("profile.enabled")
                : t("profile.disabled")}
            </span>
            <hr className="my-4 border-light" />
          </div>

          {/* Right Panel */}
          <div className="col-md-8">
            <ul className="nav nav-tabs mb-3">
              <li className="nav-item">
                <button
                  className={`nav-link ${activeTab === "info" ? "active" : ""}`}
                  onClick={() => setActiveTab("info")}>
                  {t("profile.tabs.info")}
                </button>
              </li>
              <li className="nav-item">
                <button
                  className={`nav-link ${activeTab === "bank" ? "active" : ""}`}
                  onClick={() => setActiveTab("bank")}>
                  {t("profile.tabs.bank")}
                </button>
              </li>
              <li className="nav-item">
                <button
                  className={`nav-link ${activeTab === "pack" ? "active" : ""}`}
                  onClick={() => setActiveTab("pack")}>
                  {t("profile.tabs.pack")}
                </button>
              </li>
            </ul>

            {/* Tab Content */}
            {activeTab === "info" && (
              <PersonalInfoTab user={currentUser.data} />
            )}

            {activeTab === "bank" && (
              <>
                {isBanksLoading && <p>{t("profile.loadingBanks")}</p>}
                {isBanksError && <p>{t("profile.errorBanks")}</p>}
                {!isBanksLoading && !isBanksError && (
                  <>
                    <BankInfoTab
                      bank={null}
                      onAdd={() => setShowCreateModal(true)}
                    />
                    {banksData?.data?.length > 0 ? (
                      <>
                        {banksData.data.map((bank) => (
                          <div
                            key={bank._id}
                            onClick={() => {
                              setBankToSelect(bank);
                              setShowSelectModal(true);
                            }}>
                            <BankInfoTab
                              bank={bank}
                              isSelected={bank.status === true}
                              onEdit={(b) => setEditingBank(b)}
                              onDelete={handleRequestDeleteBank}
                            />
                          </div>
                        ))}
                      </>
                    ) : (
                      <p className="text-dark">{t("profile.noBanks")}</p>
                    )}
                  </>
                )}
              </>
            )}

            {activeTab === "pack" && (
              <PackInfoTab
                pack={currentUser.data.roleId?.packId[0]}
                companyId={currentUser.data.companyId}
              />
            )}
          </div>
        </div>
      </div>

      {/* Modals */}
      {editingBank && (
        <EditBankModal
          show={!!editingBank}
          bank={editingBank}
          onClose={() => setEditingBank(null)}
          onSave={handleSaveEdit}
        />
      )}

      {showCreateModal && (
        <CreateBankModal
          show={showCreateModal}
          onClose={() => setShowCreateModal(false)}
          onCreate={handleCreateBank}
        />
      )}

      <DeleteBankModal
        show={!!bankToDelete}
        bank={bankToDelete}
        onClose={() => setBankToDelete(null)}
        onConfirm={handleConfirmDeleteBank}
      />

      <SelectBankModal
        show={showSelectModal}
        bank={bankToSelect}
        onClose={() => {
          setShowSelectModal(false);
          setBankToSelect(null);
        }}
        onConfirm={handleConfirmSelectBank}
      />

      {/* Delete Warning Modal */}
      {showDeleteWarningModal && (
        <div
          className="modal fade show d-block"
          tabIndex="-1"
          role="dialog"
          style={{ backgroundColor: "rgba(0,0,0,0.5)" }}>
          <div className="modal-dialog modal-dialog-centered" role="document">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title text-danger">
                  {t("profile.deleteWarningTitle")}
                </h5>
                <button
                  type="button"
                  className="btn-close"
                  onClick={() => setShowDeleteWarningModal(false)}></button>
              </div>
              <div className="modal-body">
                <p>{t("profile.deleteWarningMessage")}</p>
              </div>
              <div className="modal-footer">
                <button
                  type="button"
                  className="btn btn-primary"
                  onClick={() => setShowDeleteWarningModal(false)}>
                  {t("profile.close")}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default CompanyConfig;
